import dayjs, { Dayjs } from 'dayjs';

export function getDatesToCompare(value: Date, doNotCheckHours: boolean = false): { dateToTest: dayjs.Dayjs; now: dayjs.Dayjs } {
	let now = dayjs();
	let dateToTest = dayjs(value);

	// Remove time, just keep the date
	if (doNotCheckHours) {
		now = now.startOf('day');
		dateToTest = dateToTest.startOf('day');
	}

	return {
		dateToTest,
		now
	};
}

export function getDayWithoutTime(date?: Date): dayjs.Dayjs {
	return dayjs(date).startOf('day');
}

export function getDateWithoutTime(date?: Date): Date {
	return getDayWithoutTime(date).toDate();
}

export function isValidDate(d): boolean {
	return d instanceof Date && !isNaN(<any>d);
}

export function daysRange(dateStart: Date, dateEnd: Date = new Date()): Date[] {
	const range = [];
	let ticker: Date;
	for (ticker = new Date(dateStart.getTime()); ticker <= dateEnd; ticker.setDate(ticker.getDate() + 1)) {
		range.push(new Date(ticker));
	}
	return range;
}

export function compareDatesOnly(date1: Date | dayjs.Dayjs, date2: Date | dayjs.Dayjs): number {
	if (!date1 || !date2) {
		return NaN;
	}

	const date1Only = date1 instanceof Date ? getDayWithoutTime(date1) : date1;
	const date2Only = date2 instanceof Date ? getDayWithoutTime(date2) : date2;

	return date1Only.isBefore(date2Only) ? -1 : date1Only.isAfter(date2Only) ? 1 : 0;
}

export function compareDateToToday(date: Date): number {
	const daysToCompare = getDatesToCompare(date, true);
	return compareDatesOnly(daysToCompare.dateToTest, daysToCompare.now);
}

export function isDayInPast(date: Date): boolean {
	return compareDateToToday(date) < 0;
}

export function isDayToday(date: Date): boolean {
	return compareDateToToday(date) == 0;
}

export function isDayInFuture(date: Date): boolean {
	return compareDateToToday(date) > 0;
}

export function parseDate(dateString: string): Date | null {
	// Remove any non-digit characters from the input string
	const sanitizedDateString = dateString.replace(/\D/g, '');

	// Check if the sanitized string has a valid length
	if (sanitizedDateString.length !== 8) {
		return null; // Invalid date string
	}

	const day = parseInt(sanitizedDateString.substring(0, 2), 10);
	const month = parseInt(sanitizedDateString.substring(2, 4), 10) - 1; // Months are 0-indexed (January is 0, February is 1, etc.)
	const year = parseInt(sanitizedDateString.substring(4, 8), 10);

	// Check if the parsed values are valid
	if (!isNaN(day) && !isNaN(month) && !isNaN(year)) {
		return new Date(year, month, day);
	}

	return null; // Return null for invalid date strings
}

export function dateToUTC(date?: Date): Date {
	return !!date ? new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())) : null;
}

export function convertDate(inputFormat, withDash = false) {
	function pad(s) {
		return s < 10 ? '0' + s : s;
	}
	const d = new Date(inputFormat);
	return (
		pad(d.getDate()) +
		'.' +
		pad(d.getMonth() + 1) +
		'.' +
		d.getFullYear() +
		(withDash ? ' - ' : ' ') +
		pad(d.getHours()) +
		'H' +
		pad(d.getMinutes())
	);
}

export function getTimeString(date: Date): string {
	if (!date) return '';
	const h = String(date.getHours()).padStart(2, '0');
	const m = String(date.getMinutes()).padStart(2, '0');
	return `${h}:${m}`;
}
